// Planer.jsx
import React, { useEffect, useState, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import "./css/Planer.css";
import { exportPlanPDF, exportPlanJSON } from "../../functions/pdfExport_trainingsplan";
import getIntensityIcon from "../../functions/getIntensityIcon";
import { importPlanJSON } from "../../functions/importPlanJSON";

// Hilfsfunktion für eindeutige IDs
const generateUID = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "uid-" + Date.now() + "-" + Math.floor(Math.random() * 10000);

export default function Planer({
  items = [],
  setItems,
  onRemove = () => {},
  onUpdateDuration = () => {},
  onReorder = () => {},
  ageColors = {}
}) {
  const [isMounted, setIsMounted] = useState(false);
  const [trainingTitle, setTrainingTitle] = useState("");
  const [trainingAgeClass, setTrainingAgeClass] = useState("");
  const [trainingDuration, setTrainingDuration] = useState(
    items.reduce((sum, item) => sum + (item.dauer || 0), 0)
  );

  useEffect(() => {
    setTrainingDuration(items.reduce((sum, item) => sum + (item.dauer || 0), 0));
  }, [items]);

  useEffect(() => setIsMounted(true), []);

  // Stelle sicher, dass jedes Item eine uid hat
  useEffect(() => {
    const fixed = items.map((it) =>
      it.uid ? it : { ...it, uid: generateUID() }
    );
    if (fixed.some((f, i) => f.uid !== items[i]?.uid)) {
      setItems(fixed);
    }
  }, [items, setItems]);

  const DROPPABLE_ID = "planer-list-dropable";

  // Drag & Drop
  const handleDragEnd = useCallback(
    (result) => {
      if (!result || !result.destination) return;
      const src = result.source.index;
      const dest = result.destination.index;
      if (src === dest) return;

      const newOrder = Array.from(items);
      const [moved] = newOrder.splice(src, 1);
      newOrder.splice(dest, 0, moved);

      onReorder(newOrder);
    },
    [items, onReorder]
  );

  // Import
  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const { items: importedItems, info } = await importPlanJSON(file);

      // Items mit uid versehen
      const withUID = (importedItems || []).map((it) => ({
        ...it,
        uid: it.uid || generateUID(),
      }));

      setItems(withUID);
      setTrainingTitle(info.title || "");
      setTrainingAgeClass(info.ageClass || "");
      setTrainingDuration(info.duration || 0);
    } catch (err) {
      console.error("Fehler beim Import:", err);
      alert("Ungültige Datei oder Fehler beim Import.");
    }
  };

  // Pause hinzufügen
  const addPause = () => {
    const newPause = {
      uid: generateUID(),
      Titel: "Pause",
      dauer: 5,
      type: "pause",
    };
    onReorder([...items, newPause]);
  };

  return (
    <div className="planer-container">
      <h2>📝 Trainingsplan</h2>

      {/* Info-Panel */}
      <div className="planer-info">
        <input
          type="text"
          placeholder="Titel des Trainings"
          value={trainingTitle}
          onChange={(e) => setTrainingTitle(e.target.value)}
        />
        <select
          value={trainingAgeClass}
          onChange={(e) => setTrainingAgeClass(e.target.value)}
        >
          <option value="">Altersklasse wählen</option>
          {Object.keys(ageColors).map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>
        <span>Gesamtdauer: {trainingDuration} min</span>
      </div>

      <button className="planer-button planer-add-pause" onClick={addPause}>
        ➕ Pause hinzufügen
      </button>
      <button
        className="planer-button"
        onClick={() =>
          exportPlanPDF(items, {
            title: trainingTitle,
            ageClass: trainingAgeClass,
            duration: trainingDuration,
          })
        }
      >
        📄 Export PDF
      </button>
      <button
        className="planer-button"
        onClick={() =>
          exportPlanJSON(items, {
            title: trainingTitle,
            ageClass: trainingAgeClass,
            duration: trainingDuration,
          })
        }
      >
        📄 Export Config
      </button>

      <label className="upload-button">
        📂 Plan importieren
        <input
          type="file"
          accept="application/json"
          style={{ display: "none" }}
          onChange={handleImport}
        />
      </label>

      {/* Liste */}
      {isMounted ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={DROPPABLE_ID}>
            {(provided) => (
              <div
                className="planer-list"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {items.length === 0 && (
                  <p className="planer-empty">Noch keine Übungen hinzugefügt.</p>
                )}

                {items.map((uebung, index) => (
                  <Draggable
                    key={uebung.uid}
                    draggableId={String(uebung.uid)}
                    index={index}
                  >
                    {(prov) => (
                      <div
                        className={`planer-item ${
                          uebung.type === "pause" ? "planer-pause" : ""
                        }`}
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        {...prov.dragHandleProps}
                        data-index={index}
                      >
                        <div className="planer-row-top">
                          <div className="planer-duration">
                            <select
                              value={uebung.dauer}
                              onChange={(e) =>
                                onUpdateDuration(
                                  uebung.uid,
                                  Number(e.target.value)
                                )
                              }
                            >
                              {Array.from({ length: 60 }, (_, i) => i + 1).map(
                                (min) => (
                                  <option key={min} value={min}>
                                    {min} min
                                  </option>
                                )
                              )}
                            </select>
                          </div>
                          {uebung.type !== "pause" && (
                            <div className="planer-reps">
                              <span>↻</span>
                              <input
                                type="text"
                                value={uebung.Wiederholung || ""}
                                onChange={(e) => {
                                  const updated = items.map((it) =>
                                    it.uid === uebung.uid
                                      ? { ...it, Wiederholung: e.target.value }
                                      : it
                                  );
                                  setItems(updated);
                                }}
                                placeholder="Wiederholungen"
                              />
                            </div>
                          )}
                        </div>

                        <div className="planer-title">
                          <span
                            className="planer-intensity"
                            data-level={uebung.Intensität?.toLowerCase()}
                          >
                            {getIntensityIcon(uebung.Intensität)}
                          </span>
                          {uebung.type === "pause" ? "Pause" : uebung.Titel}
                        </div>

                        <button
                          className="planer-remove"
                          onClick={() => onRemove(uebung.uid)}
                          aria-label={`Entferne ${uebung.Titel}`}
                        >
                          ❌
                        </button>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div className="planer-list">
          <p className="planer-empty">Initialisiere...</p>
        </div>
      )}
    </div>
  );
}
